import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getControlCenter } from "../services/api";
import { EventDialog } from "../components/employees/EventDialog";
import MobileHome from "../components/MobileHome";
import { toast } from "sonner";
import {
  CalendarDays, Plus, Receipt, FileCheck, Users, Megaphone, MapPin,
  AlertTriangle, Loader2, ChevronRight, CircleCheck, CircleOff, GraduationCap, Coffee, Sparkles,
} from "lucide-react";
import { typeIcon, STATUS_META, PRIORITY_META } from "../utils/eventVisuals";

const fmtLongDate = (d) =>
  d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const fmtShortDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });

const daysLate = (ev, todayIso) => {
  const end = ev.end_date || ev.date;
  return Math.max(1, Math.round((new Date(todayIso) - new Date(end)) / 86400000));
};

const StatusBadge = ({ status }) => {
  const st = STATUS_META[status] || STATUS_META.todo;
  const Icon = st.icon;
  return (
    <span
      className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ color: st.color, backgroundColor: `${st.color}1A` }}
      data-testid={`status-badge-${status}`}
    >
      <Icon size={11} /> {st.label}
    </span>
  );
};

const PriorityDot = ({ priority }) => {
  const p = PRIORITY_META[priority] || PRIORITY_META.normal;
  return (
    <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: p.color }} title={`Priorité : ${p.label}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }}></span>
      {p.label}
    </span>
  );
};

const EventRow = ({ ev, roster, now, onClick }) => {
  const Icon = typeIcon(ev.event_type);
  const assignees = (roster || []).filter((r) => (ev.employee_ids || []).includes(r.employee_id));
  const isNow = ev.start_time && ev.end_time && now >= ev.start_time && now <= ev.end_time && ev.status !== "cancelled" && ev.status !== "done";
  const cancelled = ev.status === "cancelled";
  return (
    <button
      onClick={onClick}
      data-testid={`today-event-${ev.id}`}
      className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors group ${cancelled ? "opacity-50" : ""}`}
    >
      <div className="w-14 shrink-0 text-right">
        <p className="text-sm font-semibold text-slate-800 font-numbers">{ev.start_time || "—"}</p>
        {ev.end_time && <p className="text-[11px] text-slate-400 font-numbers">{ev.end_time}</p>}
      </div>
      <div className="w-1 self-stretch rounded-full shrink-0" style={{ backgroundColor: ev.color }}></div>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${ev.color}22` }}>
        <Icon size={15} style={{ color: ev.color }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium text-slate-900 truncate ${cancelled ? "line-through" : ""}`}>{ev.title}</p>
          {isNow && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-[#26788A]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#26788A] animate-srPulse"></span> En ce moment
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 truncate flex items-center gap-1.5 mt-0.5">
          <span>{ev.event_type}</span>
          {ev.location && (<><span>·</span><MapPin size={11} /><span className="truncate">{ev.location}</span></>)}
        </p>
      </div>
      <div className="hidden sm:flex items-center -space-x-1.5 shrink-0">
        {assignees.slice(0, 4).map((a) => (
          <span key={a.employee_id} title={`${a.first_name} ${a.last_name}`}
            className="w-6 h-6 rounded-full border-2 border-white dark:border-[#151517] flex items-center justify-center text-[9px] font-bold text-white"
            style={{ backgroundColor: a.color }}>
            {(a.first_name?.[0] || "") + (a.last_name?.[0] || "")}
          </span>
        ))}
      </div>
      <div className="hidden md:flex flex-col items-end gap-1 shrink-0">
        <StatusBadge status={ev.status || "todo"} />
        <PriorityDot priority={ev.priority || "normal"} />
      </div>
      <ChevronRight size={15} className="text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
    </button>
  );
};

const ROSTER_META = {
  work: { label: "Présents", icon: CircleCheck, color: "#10B981" },
  absence: { label: "Absents", icon: CircleOff, color: "#EF4444" },
  school: { label: "École", icon: GraduationCap, color: "#F59E0B" },
  off: { label: "Repos", icon: Coffee, color: "#9CA3AF" },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, canAccessTab } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventDialog, setEventDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getControlCenter();
      setData(res.data);
    } catch {
      toast.error("Erreur lors du chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = new Date();
  const nowHHMM = `${String(today.getHours()).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}`;
  const firstName = (user?.name || "").split(" ")[0];
  const canManageEvents = user?.role === "admin" || !!user?.can_manage_events;

  const roster = data?.roster || [];
  const present = roster.filter((r) => r.status === "work");
  const away = roster.filter((r) => r.status !== "work");

  const upcomingByDay = (data?.upcoming || []).reduce((acc, ev) => {
    (acc[ev.date] = acc[ev.date] || []).push(ev);
    return acc;
  }, {});

  const goPlanning = (dateIso) => navigate(`/employes/planning?date=${dateIso}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Loader2 size={28} className="animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <>
      {/* Version mobile : accueil PWA avec tuiles d'accès */}
      <div className="lg:hidden">
        <MobileHome data={data} onNewEvent={canManageEvents ? () => setEventDialog(true) : undefined} />
      </div>

      <div className="hidden lg:block p-5 lg:p-10 max-w-[1440px] mx-auto overflow-y-auto h-screen" data-testid="control-center">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] font-medium text-slate-400 capitalize">{fmtLongDate(today)}</p>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900 mt-1.5">
            Bonjour {firstName}
          </h1>
        </div>
        <div className="flex items-center gap-2.5">
          {canManageEvents && (
          <button
            onClick={() => setEventDialog(true)}
            data-testid="cc-new-event-btn"
            className="flex items-center gap-2 text-sm font-medium bg-[#26788A] text-white px-4 py-2.5 rounded-lg hover:bg-[#2E8FA3] transition-colors"
          >
            <Plus size={15} /> Nouvel événement
          </button>
          )}
          {canAccessTab("invoices") && (
          <button
            onClick={() => navigate("/document/new/invoice")}
            data-testid="cc-new-invoice-btn"
            className="flex items-center gap-2 text-sm font-medium bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            <Receipt size={15} /> Nouvelle facture
          </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-16">
        {/* Colonne principale */}
        <div className="lg:col-span-8 space-y-6">
          {/* Aujourd'hui */}
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="today-timeline">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <CalendarDays size={17} className="text-[#26788A]" /> Aujourd'hui
                <span className="text-xs font-normal text-slate-400">{(data?.events || []).length} événement{(data?.events || []).length > 1 ? "s" : ""}</span>
              </h2>
              {canAccessTab("employees") && (
              <button onClick={() => goPlanning(data?.date)} className="text-xs font-medium text-[#26788A] hover:underline" data-testid="open-planning-link">
                Ouvrir le planning
              </button>
              )}
            </div>
            {(data?.events || []).length === 0 ? (
              <div className="py-14 text-center">
                <Sparkles size={26} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Aucun événement aujourd'hui.</p>
                <button onClick={() => setEventDialog(true)} className="text-xs font-medium text-[#26788A] hover:underline mt-1.5">
                  Planifier quelque chose
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.events.map((ev) => (
                  <EventRow key={ev.id} ev={ev} roster={roster} now={nowHHMM} onClick={() => goPlanning(ev.date)} />
                ))}
              </div>
            )}
          </section>

          {/* 7 prochains jours */}
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="upcoming-events">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-900">7 prochains jours</h2>
            </div>
            {Object.keys(upcomingByDay).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">Rien de planifié cette semaine.</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {Object.entries(upcomingByDay).map(([day, evs]) => (
                  <div key={day} className="px-5 py-3.5">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 capitalize mb-2">{fmtShortDate(day)}</p>
                    <div className="space-y-1.5">
                      {evs.map((ev) => {
                        const Icon = typeIcon(ev.event_type);
                        const cancelled = ev.status === "cancelled";
                        return (
                          <button key={ev.id} onClick={() => goPlanning(day)} data-testid={`upcoming-event-${ev.id}`}
                            className={`w-full flex items-center gap-2.5 text-left px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors ${cancelled ? "opacity-50" : ""}`}>
                            <span className="w-5 h-5 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${ev.color}22` }}>
                              <Icon size={11} style={{ color: ev.color }} />
                            </span>
                            <span className={`text-sm text-slate-700 truncate flex-1 ${cancelled ? "line-through" : ""}`}>{ev.title}</span>
                            {ev.start_time && <span className="text-xs text-slate-400 font-numbers">{ev.start_time}</span>}
                            <StatusBadge status={ev.status || "todo"} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Colonne droite */}
        <div className="lg:col-span-4 space-y-6">
          {/* Équipe aujourd'hui */}
          {canAccessTab("employees") && (
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="team-roster">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Users size={17} className="text-[#26788A]" /> Équipe
              </h2>
              <span className="text-xs text-slate-400">{present.length}/{roster.length} présent{present.length > 1 ? "s" : ""}</span>
            </div>
            <div className="p-4 space-y-4">
              {roster.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Aucun employé actif</p>}
              {present.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5" style={{ color: ROSTER_META.work.color }}>
                    <CircleCheck size={12} /> Présents
                  </p>
                  <div className="space-y-1">
                    {present.map((r) => (
                      <div key={r.employee_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg" data-testid={`roster-present-${r.employee_id}`}>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ backgroundColor: r.color }}>
                          {(r.first_name?.[0] || "") + (r.last_name?.[0] || "")}
                        </span>
                        <span className="text-sm text-slate-800 flex-1 truncate">{r.first_name} {r.last_name}</span>
                        <span className="text-[11px] text-slate-400 font-numbers whitespace-nowrap">{r.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {away.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2 flex items-center gap-1.5">
                    <CircleOff size={12} /> Absents & indisponibles
                  </p>
                  <div className="space-y-1">
                    {away.map((r) => {
                      const meta = ROSTER_META[r.status] || ROSTER_META.off;
                      const Icon = meta.icon;
                      return (
                        <div key={r.employee_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg opacity-80" data-testid={`roster-away-${r.employee_id}`}>
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 grayscale-[35%]" style={{ backgroundColor: r.color }}>
                            {(r.first_name?.[0] || "") + (r.last_name?.[0] || "")}
                          </span>
                          <span className="text-sm text-slate-600 flex-1 truncate">{r.first_name} {r.last_name}</span>
                          <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: `${meta.color}1A` }}>
                            <Icon size={10} /> {r.detail}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>
          )}

          {/* Tâches en retard */}
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="late-tasks">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-500" /> En retard
              </h2>
              {(data?.late_tasks || []).length > 0 && (
                <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">{data.late_tasks.length}</span>
              )}
            </div>
            {(data?.late_tasks || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Rien en retard. Bravo !</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.late_tasks.map((ev) => {
                  const Icon = typeIcon(ev.event_type);
                  return (
                    <button key={ev.id} onClick={() => goPlanning(ev.date)} data-testid={`late-task-${ev.id}`}
                      className="w-full flex items-center gap-2.5 text-left px-5 py-3 hover:bg-slate-50 transition-colors">
                      <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${ev.color}22` }}>
                        <Icon size={12} style={{ color: ev.color }} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 truncate">{ev.title}</p>
                        <p className="text-[11px] text-slate-400">{fmtShortDate(ev.date)}</p>
                      </div>
                      <span className="text-[10px] font-semibold text-red-500 whitespace-nowrap">
                        {daysLate(ev, data.date)} j de retard
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Accès rapides */}
          <section className="grid grid-cols-2 gap-3" data-testid="quick-links">
            {[
              { label: "Factures", icon: Receipt, to: "/documents/invoice", tab: "invoices" },
              { label: "Devis", icon: FileCheck, to: "/documents/quote", tab: "quotes" },
              { label: "Clients", icon: Users, to: "/clients", tab: "clients" },
              { label: "Communication", icon: Megaphone, to: "/communication", tab: "communication" },
              { label: "Calendrier de diffusion", icon: CalendarDays, to: "/calendrier-diffusion", tab: "content_calendar" },
              { label: "Notes de frais", icon: Receipt, to: "/notes-de-frais", tab: "expenses" },
            ].filter((l) => canAccessTab(l.tab)).map((l) => (
              <button key={l.to} onClick={() => navigate(l.to)} data-testid={l.tab === "content_calendar" ? "quick-link-content-calendar" : l.tab === "expenses" ? "quick-link-expenses" : `quick-link-${l.label.toLowerCase()}`}
                className="flex min-w-0 items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 hover:border-[#26788A] hover:text-[#26788A] transition-colors">
                <l.icon size={16} className="shrink-0" /> {l.label}
              </button>
            ))}
          </section>
        </div>
      </div>

      </div>

      <EventDialog
        open={eventDialog}
        onClose={() => setEventDialog(false)}
        defaultDate={data?.date}
        onSaved={load}
      />
    </>
  );
}
